import { createContext, PropsWithChildren, useCallback, useReducer } from 'react'
import { PaymentInfoWrapper } from '../../wrappers'
import {
  AddressInfoData,
  ContactInfoData,
  OfferValue,
  PaymentInfoData,
  PaymentTypes,
  PickaxeClaimResult,
  PickaxeClaimResultItem,
  RebillyTokenData
} from '../../models'

import { forget } from '@xylabs/forget'
import { useCoinUserEvents } from '../../../tracking'
import { Cart, CartInfo, CartItem, CartItemSlot, useCart } from '../../hooks'
import { createPaymentTokenParams, processPaymentGpc, ProcessPaymentParams, hasTrial } from '../../checkout'
import { UtmFields } from '@xylabs/pixel'
import { assertEx } from '@xylabs/sdk-js'
import { isLocalhost } from 'app-logic/http/helpers/isLocalHost'
import { log } from 'app-logic/logging'
import { createPaymentToken } from '../../checkout/rebilly'
import axios, { AxiosError } from 'axios'
import { useMounted } from 'app-logic/react/useMounted'
import { setLocalStorageObject } from '../../../../app-logic/storage'
import { createCustomFields } from '../customFields'

//pass in default value
export const paymentInfoWrapper = new PaymentInfoWrapper({ creditcard: '', expiry: '', cvc: '' }).load()


enum PaymentActions {
  SET_CARD,
  SET_EXP,
  SET_CVC,
  SET_OFFER,
  SET_PAYMENT_TOKEN_DATA,
  SET_TERMS,
  SET_ERRORS,
  CLEAR_ERRORS,
  SET_TOKEN,
  SET_PAYMENT_TYPE,
  SET_PICKAXE
}

type PaymentAction =
  | { type: PaymentActions.SET_CARD, payload: string }
  | { type: PaymentActions.SET_EXP, payload: string }
  | { type: PaymentActions.SET_CVC, payload: string }
  | { type: PaymentActions.SET_OFFER, payload: OfferValue }
  | { type: PaymentActions.SET_PAYMENT_TOKEN_DATA, payload: RebillyTokenData | undefined }
  | { type: PaymentActions.SET_TERMS, payload: boolean }
  | { type: PaymentActions.SET_ERRORS, payload: Error[] }
  | { type: PaymentActions.CLEAR_ERRORS }
  | { type: PaymentActions.SET_TOKEN, payload: string | undefined }
  | { type: PaymentActions.SET_PAYMENT_TYPE, payload: PaymentTypes }
  | { type: PaymentActions.SET_PICKAXE, payload: PickaxeClaimResultItem }


interface PaymentInfoDataExtended extends  PaymentInfoData {
  errors?: Error[]
  token?: string
  paymentType?: PaymentTypes
  pickaxe?: PickaxeClaimResultItem
}

const PaymentReducer = (state: PaymentInfoDataExtended , action: PaymentAction) => {
  switch(action.type) {
    case PaymentActions.SET_CARD:
      return { ...state, creditcard: action.payload, errors: undefined }
    case PaymentActions.SET_CVC:
      return { ...state, cvc: action.payload, errors: undefined }
    case PaymentActions.SET_EXP:
      return { ...state, expiry: action.payload, errors: undefined }
    case PaymentActions.SET_OFFER:
      return { ...state, offer: action.payload, errors: undefined }
    case PaymentActions.SET_PAYMENT_TOKEN_DATA:
      return { ...state, payFrameData: action.payload, errors: undefined }
    case PaymentActions.SET_TERMS:
      return { ...state, termsAccepted: action.payload, errors: undefined }
    case PaymentActions.SET_ERRORS:
      return { ...state, token: undefined, errors: [ ...(state.errors ?? []), ...action.payload ] }
    case PaymentActions.CLEAR_ERRORS:
      return { ...state, errors: undefined }
    case PaymentActions.SET_TOKEN:
      return { ...state, token: action.payload }
    case PaymentActions.SET_PAYMENT_TYPE:
      return { ...state, errors: undefined, paymentType: action.payload }
    case PaymentActions.SET_PICKAXE:
      sessionStorage.setItem('pickaxeClaim', JSON.stringify(action.payload))
      return { ...state, pickaxe: action.payload }
    default:
      return state
  }
}

interface Props {
  data?: PaymentInfoDataExtended
  setCreditCard: (value: string) => void
  setCvc: (value: string) => void
  setExp: (value: string) => void
  setOffer: (offer: OfferValue) => void
  setPayFrameData: (token?: RebillyTokenData) => void
  setTermsAccepted: (accepted: boolean) => void
  setPaymentType: (type: PaymentTypes) => void
  setErrors: (errors: Error[]) => void
  clearErrors: () => void
  onError: (error: Error) => void
  errors: () =>  Promise<Error[] | undefined>
  onProcessPayment: (contactInfoData: ContactInfoData, addressInfoData: AddressInfoData) => Promise<void>
  onProcessUpsell: (
    item: CartItem,
    contactInfoData: ContactInfoData,
    addressInfoData: AddressInfoData,
    customFields?: Record<string, string>,
  ) => Promise<void>
  claimPickaxe: (receipt: string) => Promise<void>
}

export const PaymentContext = createContext<Props>({
  data: {},
  setCreditCard: (value: string) => {},
  setCvc: (value: string) => {},
  setExp: (value: string) => {},
  setOffer: (offer: OfferValue) => {},
  setPayFrameData: (token?) => {},
  setTermsAccepted: (accepted: boolean) => {},
  setPaymentType: (acctypeepted: PaymentTypes) => {},
  setErrors: (errors: Error[]) => {},
  onError: (error) => {},
  clearErrors: () => {},
  errors: () => Promise.resolve(undefined),
  onProcessPayment: (contactInfoData: ContactInfoData, addressInfoData: AddressInfoData) => Promise.resolve(),
  onProcessUpsell: (
    item: CartItem,
    contactInfoData: ContactInfoData,
    addressInfoData: AddressInfoData,
    customFields?: Record<string, string>,
  ) => Promise.resolve(),
  claimPickaxe: (receipt: string) => Promise.resolve() 
})
export const PaymentProvider: React.FC<PropsWithChildren> = ({ children }) => {
  const paymentInfo = { ...paymentInfoWrapper.data, paymentType: PaymentTypes.CARD }
  const [state, dispatch] = useReducer(PaymentReducer, paymentInfo)
  const userEvents = useCoinUserEvents()
  const cart = useCart()

  const setCreditCard = useCallback((value: string) => {
    paymentInfoWrapper.setCreditCard(value)
    dispatch({ type: PaymentActions.SET_CARD, payload: value })
  }, [dispatch])

  const setCvc = useCallback((value: string) => {
    paymentInfoWrapper.setCvc(value)
    dispatch({ type: PaymentActions.SET_CVC, payload: value })
  }, [dispatch])

  const setExp = useCallback((value: string) => {
    paymentInfoWrapper.setExpiry(value)
    dispatch({ type: PaymentActions.SET_EXP, payload: value })
  }, [dispatch])

  const setOffer = (offer: OfferValue) => {
    paymentInfoWrapper.setOffer(offer)
    dispatch({ type: PaymentActions.SET_OFFER, payload: offer })
  }
  const setPayFrameData = (token?: RebillyTokenData) => {
    paymentInfoWrapper.setPayFrameData(token)
    paymentInfoWrapper.setToken(token?.id)
    dispatch({ type: PaymentActions.SET_PAYMENT_TOKEN_DATA, payload: token })
    dispatch({ type: PaymentActions.SET_TOKEN, payload: token?.id })
  }

  const setTermsAccepted = (accepted: boolean) => {
    paymentInfoWrapper.setTermsAccepted(accepted)
    dispatch({ type: PaymentActions.SET_TERMS, payload: accepted })
  }

  const setPaymentType = (type: PaymentTypes) => {
    // TODO - something in payment info wrapper to set type for errors check
    dispatch({ type: PaymentActions.SET_PAYMENT_TYPE, payload: type })
    switch (type) {
      case PaymentTypes.CARD:
        setPayFrameData()
        break
      case PaymentTypes.GOOGLE:
      case PaymentTypes.APPLE:
        setCvc('')
        setExp('')
        setCreditCard('')

    }
  }
  const setErrors = (errors: Error[]) => {
    dispatch({ type: PaymentActions.SET_ERRORS, payload: errors })
  }

  const clearErrors = () => {
    dispatch({ type: PaymentActions.CLEAR_ERRORS })
  }

  const onError = (error: Error) => {
    dispatch({ type: PaymentActions.SET_ERRORS, payload: [error] })
  }
  const errors = async () => {
    return [...(state.paymentType === PaymentTypes.CARD ? await paymentInfoWrapper.errors() : []), ...state.errors ?? []]
  }

  const onProcessPayment = async (contactInfoData: ContactInfoData, addressInfoData: AddressInfoData) => {
    try {
      console.log('on process payment')
      forget(userEvents.purchaseAttempted())
      console.log('why me?')
      if (state.cvc) {
        sessionStorage.setItem('cvc', state.cvc)
      }
      // running into an issue with state updates, so falling back to paymentInfoWrapper
      const params = createPaymentParams(contactInfoData, addressInfoData, paymentInfoWrapper.data, cart)
      let token
      if (paymentInfoWrapper.data.payFrameData) {
        token = paymentInfoWrapper.data.payFrameData?.id
      } else {
        const result = await createPaymentToken(createPaymentTokenParams(params))
        paymentInfoWrapper.setToken(result)
        token = result
      }
      dispatch({ type: PaymentActions.SET_TOKEN, payload: token })
      const result = await processPaymentGpc({ ...params, paymentToken: token })
      if(result.errors) {
        forget(userEvents.purchaseFailed())
        reportErrors(result.errors)
        dispatch({ type: PaymentActions.SET_ERRORS, payload: result.errors })
        throw result.errors
      } else {
        if (!hasTrial(result)) {
          cart.removeItem(CartItemSlot.Trial)
          cart.save()
        } else {
          forget(userEvents.trialStarted())
          console.warn('Has Trail is only set up to check for PRO trail')
        }

        const paymentInstrument = result.data?.checkout?.transaction?.paymentInstrument
        if (paymentInstrument) {
          paymentInfoWrapper.setPaymentInstrument(paymentInstrument)
        }

        forget(userEvents.purchaseCompleted(result))
        // saves the rid to local storage
        localStorage.setItem('_coin_rid', result.data?.checkout?.customer?.id || '')
        //Claim pickaxe
        const transactionId = result.data?.checkout?.transaction?.id
        if (transactionId && cart.getItemsBySlot(CartItemSlot.NFT)) {
          await claimPickaxe(transactionId)
        }
      }
    } catch(ex) {
      if (isMounted()) {
        const previousErrors = await errors()
        const error = ex as AxiosError
        if (axios.isAxiosError(ex)) {
          const castError = error as { response?: { data?: { details: string[] } } }
          dispatch({ type: PaymentActions.SET_ERRORS, payload: (castError.response?.data?.details.map((item: string) => Error(item)) ?? []).concat(previousErrors)})
        } else {
          dispatch({ type: PaymentActions.SET_ERRORS, payload: [error, ...previousErrors] })
        }
        throw ex
      }
    }
  }

  const claimPickaxe = async (transactionId: string) => {
    const body = {
      receipt: transactionId
    }
    const pickAxeResult = await axios.post<PickaxeClaimResult>(
      `${window.location.host === 'coinapp.co' ? 'https://prod.coinapp.co/pickaxes' : 'https://prod.coinapp.co/pickaxes-mumbai'}/v1/claim/rebilly`,
        body
    )
    if (pickAxeResult.data.success) {
      dispatch({ type: PaymentActions.SET_PICKAXE, payload: pickAxeResult.data.claims[0] })
    }
  }


  // Checkout code
  const isMounted = useMounted()
  const isNotProduction = isLocalhost() || window.location.hostname === 'beta.coinapp.co'

  const createPaymentParams = (contactInfoData: ContactInfoData, addressInfoData: AddressInfoData, paymentInfoData: PaymentInfoData, cart: Cart) => {
    const utmFields = new UtmFields()
    const utmFieldData = (utmFields.fields ?? []).slice(-1)[0]
    const params: ProcessPaymentParams = {
      billing: addressInfoData.billing,
      shipping: addressInfoData.sameAddress ? addressInfoData.billing : addressInfoData.shipping,
      cartInfo: cart.data,
      customFields: createCustomFields(utmFieldData),
      customer: contactInfoData,
      leadSource: {
        campaign: utmFieldData?.['campaign'],
        content: utmFieldData?.['content'],
        medium: utmFieldData?.['medium'],
        path: window.location.pathname,
        source: utmFieldData?.['source'],
        term: utmFieldData?.['term'],
      },
      log,
      paymentInfo: {
        creditcard: paymentInfoData.payFrameData ? 'PAY_FRAME' : assertEx(paymentInfoData.creditcard, 'Missing cc number'),
        cvc: paymentInfoData.payFrameData ? 'PAY_FRAME' : assertEx(paymentInfoData.cvc, 'Missing cvc'),
        expiry: paymentInfoData.payFrameData ? 'PAY_FRAME' : assertEx(paymentInfoData.expiry, 'Missing expiry'),
      },
      sandbox: isNotProduction,
    }
    return params
  }
  
  const reportErrors = (errors: Error[]) => {
    const apolloError = errors.filter((error) => {
      const e = error as ApolloErrorInterface
      if (e?.extensions && e?.extensions?.code && (e?.extensions?.code === 'BAD_USER_INPUT' || e?.extensions?.code === 'INTERNAL_SERVER_ERROR')) {
        return error
      }
      return false
    })
    if (apolloError.length) {
      log.error(apolloError)
    }
  }
  interface ApolloErrorInterface {
    extensions?: ApolloErrorExtensions
    message: string
  }
  interface ApolloErrorExtensions {
    code:
      | 'GRAPHQL_PARSE_FAILED'
      | 'GRAPHQL_VALIDATION_FAILED'
      | 'BAD_USER_INPUT'
      | 'UNAUTHENTICATED'
      | 'FORBIDDEN'
      | 'PERSISTED_QUERY_NOT_FOUND'
      | 'PERSISTED_QUERY_NOT_SUPPORTED'
      | 'INTERNAL_SERVER_ERROR'
  }

  const onProcessUpsell = async (
    item: CartItem,
    contactInfoData: ContactInfoData,
    addressInfoData: AddressInfoData,
    customFields?: Record<string, string>,
  ) => {
    debugger
    // TODO - for standard transactions we generate a payment token then send the token, this is not compatible with PayFrame
    const params: ProcessPaymentParams = {
      billing: addressInfoData.billing,
      shipping: addressInfoData.sameAddress ? addressInfoData.billing : addressInfoData.shipping,
      cartInfo: { items: [item] },
      customFields,
      customer: contactInfoData,
      // paymentInfo: {
      //   creditcard: assertEx(paymentInfoWrapper.data.creditcard, 'Missing cc number'),
      //   cvc: assertEx(paymentInfoWrapper.data.cvc, 'Missing cvc'),
      //   expiry: assertEx(paymentInfoWrapper.data.expiry, 'Missing expiry'),
      // },
      paymentInstrument: {
        paymentCardId: assertEx(paymentInfoWrapper.data.paymentInstrument?.paymentCardId, 'Missing payment card'),
        method: assertEx(paymentInfoWrapper.data.paymentInstrument?.method, 'Missing payment method'),     
      },
      sandbox: isNotProduction,
    }

    const processPaymentResult = await processPaymentGpc(params)
    if(processPaymentResult.errors && processPaymentResult.errors.length) {
      throw processPaymentResult.errors[0]
    }
    const cartInfo: CartInfo = {
      items: [item],
      margin: (item.value ?? 0) - (item.COGS ?? 0),
      total: item.price,
      value: item.value,
    }
    cart.data.items = cartInfo.items
    setLocalStorageObject('cartInfo', cartInfo)
    sessionStorage.setItem('transactionId', processPaymentResult?.data?.checkout?.transaction?.id ?? '')
  }

  return (
    <PaymentContext.Provider value={{
      data: state,
      errors,
      setCreditCard,
      setCvc,
      setExp,
      setOffer,
      setPayFrameData,
      setTermsAccepted,
      setPaymentType,
      setErrors,
      clearErrors,
      onProcessPayment,
      onProcessUpsell,
      onError,
      claimPickaxe,
    }}>
      {children}
    </PaymentContext.Provider>
  )
}